import { geocodeByAddress } from "react-places-autocomplete"
import { ref, get } from "firebase/database"
import memoize from "memoizee"
import {
  getPackagePricingFromPriceScheme,
  NextPriceScheme,
  NextRegionScheme,
  NextRegionSchemeWithId,
  PackagePricing,
  LessonTypeName,
} from "@coast/utils"

import { DRIVING_MINUTE_PRODUCT_ID } from "../components/admin/common/constants"
import { phoneNumber, schedulingApiKey } from "../config"
import apiConfig from "../apiConfig"
import { sdk9db } from "../firebase"
import {
  getDmvPackages,
  getSharedPackages,
  getStandardPackages,
  getRoadTestPackages,
  getRoadTestWithCarPackages,
  STATE_FILTERS,
  fetchPackages,
} from "../packages"
import {
  Coordinates,
  LocationCoordsCache,
  Geocode,
  CourseType,
  ProductPackageWithPrices,
} from "../types"
import { minutesToHours } from "./misc"
import { makeApiCall } from "./gatewayUtils"
import { CouponsState } from "../components/CouponsProvider"
import { getCourseByType } from "../components/template-parts/drivers-ed/content"
import { GQLProductPackageFieldsFragment } from "../graphqlTypes"
import { logErrorMessage } from "./errors"

export const getTravelStatsFromCoords = (
  fromCoords: Coordinates,
  toCoords: Coordinates,
) => {
  // Note that this returns duration without traffic.
  if (
    !fromCoords ||
    !fromCoords.lat ||
    !fromCoords.lng ||
    !toCoords ||
    !toCoords.lat ||
    !toCoords.lng
  ) {
    throw new Error("Missing lat/long for one or more location")
  }

  const payload = {
    fromLng: fromCoords.lng,
    fromLat: fromCoords.lat,
    toLng: toCoords.lng,
    toLat: toCoords.lat,
    removeTraffic: true,
  }

  return makeApiCall(apiConfig.travelStats, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  })
}

export const isPointInside = (
  point: Coordinates,
  vs: Array<[number, number]>,
): boolean => {
  // ray-casting algorithm based on
  // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html

  const { lng: x, lat: y } = point

  let inside = false
  // eslint-disable-next-line no-plusplus
  for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
    const xi = vs[i][0]
    const yi = vs[i][1]
    const xj = vs[j][0]
    const yj = vs[j][1]

    const intersect =
      yi > y !== yj > y &&
      // eslint-disable-next-line
      x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }

  return inside
}

const MEMOIZE_MS = 1000 * 60 * 60 * 24 // 24 hours

export interface InMarketApiResponse {
  in_market: boolean
  provider_ids: string[]
}

export interface InMarketResult {
  inMarket: boolean
  providerIds: string[]
}

export const inMarket = memoize(
  (zipcode: number | string): Promise<InMarketResult> =>
    makeApiCall(`${apiConfig.inMarket}?address=${zipcode}`, {
      method: "GET",
      headers: {
        "api-key": schedulingApiKey,
      },
    }).then((result: InMarketApiResponse) => ({
      inMarket: result.in_market,
      providerIds: result.provider_ids,
    })),
  { maxAge: MEMOIZE_MS, promise: true },
)

export const geocodeFromPartialAddress = memoize(
  (address: string) =>
    makeApiCall(
      apiConfig.geocodeFull,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          address,
        }),
      },
      {
        silenceErrors: true,
      },
    ),
  { maxAge: MEMOIZE_MS, promise: true },
)

export async function reverseGeocode(
  lat: number,
  lng: number,
): Promise<LocationCoordsCache> {
  return makeApiCall(apiConfig.reverseGeocode, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      lat,
      lng,
    }),
  }).then((result) => result)
}

export const getPriceScheme = (
  priceSchemeId: string,
): Promise<NextPriceScheme | null> =>
  get(ref(sdk9db, `priceSchemesV2/${priceSchemeId}`)).then((snap) => {
    if (!snap.exists()) {
      return null
    }

    return snap.val() as NextPriceScheme
  })

export const regionFromCoords = (
  coords: Coordinates,
): Promise<NextRegionSchemeWithId | null> =>
  // Given the lng/lat coords for an address, which region does it belong to?
  get(ref(sdk9db, "regionSchemeV2")).then((regionsSnap) => {
    let matchedRegion: NextRegionSchemeWithId | null = null
    regionsSnap.forEach((region) => {
      const regionScheme = {
        ...(region.val() as NextRegionScheme),
        id: region.key,
      }
      const coordinates = regionScheme?.geoJsonFeature?.geometry.coordinates

      if (!coordinates) {
        return
      }

      const coordList = coordinates[0]

      if (isPointInside(coords, coordList)) {
        matchedRegion = regionScheme
      }
    })

    return matchedRegion
  })

interface GetPackagePricesForCoordinatesResult {
  region: NextRegionSchemeWithId | null
  priceScheme: NextPriceScheme | null
}

async function getPackagePricesForCoordinates(
  coordinates: Coordinates,
): Promise<GetPackagePricesForCoordinatesResult> {
  const region = await regionFromCoords(coordinates)

  if (!region?.priceSchemeId) {
    return { priceScheme: null, region }
  }

  const priceScheme = await getPriceScheme(region.priceSchemeId)

  return { priceScheme, region }
}

export const getPackagesPricingFactory = async (
  coordinates: Coordinates,
  { courseCoupons, lessonCoupons, roadTestCoupons }: CouponsState,
) => {
  const { priceScheme, region } =
    await getPackagePricesForCoordinates(coordinates)
  return (
    packages: GQLProductPackageFieldsFragment[],
    couponCode?: string,
  ): PackagePricing | null => {
    if (!priceScheme) {
      return null
    }

    return getPackagePricingFromPriceScheme(
      packages,
      priceScheme,
      region,
      couponCode
        ? (courseCoupons?.[couponCode] ??
            lessonCoupons?.[couponCode] ??
            roadTestCoupons?.[couponCode])
        : undefined,
    )
  }
}

const replaceFirstPackageWithCustomDuration = (
  packages: ProductPackageWithPrices[] | null,
  totalMinutes: number,
  pricePerHour: number,
): ProductPackageWithPrices[] | null => {
  if (packages === null) {
    return null
  }

  const pack = packages[0]

  if (
    !pack ||
    totalMinutes === null ||
    totalMinutes === undefined ||
    totalMinutes === pack.lessonPackage?.totalMinutes
  ) {
    return packages
  }

  const price = Math.floor(pricePerHour * minutesToHours(totalMinutes))

  return packages.map((next, index) => {
    if (index !== 0) {
      return next
    }

    return {
      ...pack,
      ...(pack.lessonPackage && {
        lessonPackage: {
          ...pack.lessonPackage,
          totalMinutes,
          drivingMinutes: totalMinutes,
        },
      }),
      id: DRIVING_MINUTE_PRODUCT_ID,
      price,
      msrp: price,
      discountAmount: 0,
    }
  })
}

function hasPackagePricesForLocation(
  packages: GQLProductPackageFieldsFragment[],
  priceScheme: NextPriceScheme,
): boolean {
  const prices = priceScheme.products

  return packages.some((next) => prices[next.id] !== undefined)
}

export const maybeGetDoNotServiceMessage = async (
  coordinates: Coordinates,
  stateRaw: string,
): Promise<string | null> => {
  // checks if we serve a state(i.e. have packages in that state)
  // then if we have prices for a location within the state
  // if we have no packages, we return early with no service
  // if we have packages but no prices we assume not offering lessons there yet?
  const state = stateRaw.toLowerCase()
  const DONT_SERVICE_STATE_MSG =
    "We're sorry, we don't currently offer driving lessons in your state."
  const DONT_SERVICE_AREA_MSG = `Looks like we don't have availability in your area. Please contact a support member at ${phoneNumber}`

  const doServeState = state ? state.toUpperCase() in STATE_FILTERS : false
  if (!doServeState) {
    return DONT_SERVICE_STATE_MSG
  }

  const { priceScheme } = await getPackagePricesForCoordinates(coordinates)
  if (!priceScheme) {
    return DONT_SERVICE_AREA_MSG
  }

  const packages = await fetchPackages(state)

  const doHavePackagePrices = hasPackagePricesForLocation(packages, priceScheme)
  if (!doHavePackagePrices) {
    return DONT_SERVICE_AREA_MSG
  }
  return null
}

type LocationPrices = {
  [K in LessonTypeName]: ProductPackageWithPrices[] | null
} & { updatedAt: number }

export const getLocationPricesFactory = async (
  couponState: CouponsState,
  coordinates: Coordinates,
) => {
  const getPackagesPricing = await getPackagesPricingFactory(
    coordinates,
    couponState,
  )

  return async (
    overrideSingleDurationMinutes: null | number = null,
    stateRaw: string,
  ): Promise<LocationPrices | null> => {
    const state = stateRaw.toLowerCase()
    const packages = await fetchPackages(state)

    if (!packages) {
      return null
    }

    const standardPackagesPricing = getPackagesPricing(
      getStandardPackages(packages),
    )
    const drivingTestPackagesPricing = getPackagesPricing(
      getDmvPackages(packages),
    )
    const sharedPackagesPricing = getPackagesPricing(
      getSharedPackages(packages),
    )
    const roadTest30PackagesPricing = getPackagesPricing(
      getRoadTestPackages(packages, 30),
    )
    const roadTest15PackagesPricing = getPackagesPricing(
      getRoadTestPackages(packages, 15),
    )
    const roadTest30WithCarPackagesPricing = getPackagesPricing(
      getRoadTestWithCarPackages(packages, 30),
    )
    const roadTest15WithCarPackagesPricing = getPackagesPricing(
      getRoadTestWithCarPackages(packages, 15),
    )
    const prepTest60PackagesPricing = getPackagesPricing(
      getRoadTestPackages(packages, 60),
    )
    const roadTest120PackagesPricing = getPackagesPricing(
      getRoadTestPackages(packages, 120),
    )
    const roadTest180PackagesPricing = getPackagesPricing(
      getRoadTestPackages(packages, 180),
    )

    if (
      !standardPackagesPricing ||
      !drivingTestPackagesPricing ||
      !sharedPackagesPricing ||
      !roadTest30PackagesPricing ||
      !roadTest15PackagesPricing ||
      !roadTest30WithCarPackagesPricing ||
      !roadTest15WithCarPackagesPricing ||
      !prepTest60PackagesPricing ||
      !roadTest120PackagesPricing ||
      !roadTest180PackagesPricing
    ) {
      return null
    }

    const pricePerHour =
      standardPackagesPricing.priceScheme.products[DRIVING_MINUTE_PRODUCT_ID] *
      60

    // @ TODO oneHour is the same as standard here
    return {
      oneHour: overrideSingleDurationMinutes
        ? replaceFirstPackageWithCustomDuration(
            standardPackagesPricing.packagesWithPrice,
            overrideSingleDurationMinutes,
            pricePerHour,
          )
        : standardPackagesPricing.packagesWithPrice,
      shared: overrideSingleDurationMinutes
        ? replaceFirstPackageWithCustomDuration(
            sharedPackagesPricing.packagesWithPrice,
            overrideSingleDurationMinutes,
            pricePerHour,
          )
        : sharedPackagesPricing.packagesWithPrice,
      "one-hour-shared": overrideSingleDurationMinutes
        ? replaceFirstPackageWithCustomDuration(
            sharedPackagesPricing.packagesWithPrice,
            overrideSingleDurationMinutes,
            pricePerHour,
          )
        : sharedPackagesPricing.packagesWithPrice,
      standard: overrideSingleDurationMinutes
        ? replaceFirstPackageWithCustomDuration(
            standardPackagesPricing.packagesWithPrice,
            overrideSingleDurationMinutes,
            pricePerHour,
          )
        : standardPackagesPricing.packagesWithPrice,
      "dmv-test": overrideSingleDurationMinutes
        ? replaceFirstPackageWithCustomDuration(
            drivingTestPackagesPricing.packagesWithPrice,
            overrideSingleDurationMinutes,
            pricePerHour,
          )
        : drivingTestPackagesPricing.packagesWithPrice,
      "road-test": roadTest30PackagesPricing.packagesWithPrice,
      "road-test-15": roadTest15PackagesPricing.packagesWithPrice,
      "road-test-with-car": roadTest30WithCarPackagesPricing.packagesWithPrice,
      "road-test-15-with-car":
        roadTest15WithCarPackagesPricing.packagesWithPrice,
      "prep-test-60": prepTest60PackagesPricing.packagesWithPrice,
      "road-test-120": roadTest120PackagesPricing.packagesWithPrice,
      "road-test-180": roadTest180PackagesPricing.packagesWithPrice,
      updatedAt: Date.now(),
    }
  }
}

export const getStateFromAddress = async (address: string) => {
  const result = await geocodeByAddress(address)
  const state =
    result?.[0].address_components.filter((next) =>
      next.types.includes("administrative_area_level_1"),
    )?.[0].short_name ?? "CA"
  return state
}

export const getCheckoutUrl = async (
  packageChoice: ProductPackageWithPrices,
  geoData: Geocode | null,
  showZipcodeError: boolean,
) => {
  if (!geoData || !geoData?.state) {
    // This shouldn't happen but it's a catch all.
    return null
  }

  let url = null

  if (geoData.zipcode) {
    url = `/checkout/${geoData.state.toLowerCase()}/${geoData.zipcode}/${packageChoice.id}`
  } else if (!packageChoice.hasLessons) {
    url = `/checkout/${geoData.state.toLowerCase()}/${packageChoice.id}`
  } else {
    if (showZipcodeError) {
      logErrorMessage("Package has no zipcode and is a lesson package", {
        packageChoice,
      })
    }
    return "/packages"
  }

  if (packageChoice.hasCourse && packageChoice.courseType) {
    const region = await regionFromCoords(geoData)
    if (region) {
      const courseMeta = getCourseByType(
        packageChoice.state,
        packageChoice.courseType as CourseType,
        region.id,
      )
      if (courseMeta) {
        url += `/course/${courseMeta.courseId}`
      }
    }
  }

  return url
}

export function extractCity(address: string | undefined): string | null {
  if (!address) return null

  const match = address.match(/^[^,]+,\s*([^,]+),\s*[A-Z]{2}(?:,)?\s*\d{5}/)
  return match ? match[1].trim() : null
}

export function extractZipcode(address: string | undefined): string | null {
  if (!address) return null

  // Match 5 digits optionally followed by -4 digits, with flexible context
  // Handles formats like: "CA 95030", "CA 95030, USA", "CA 95030 USA",
  // "CA 95030, United States", "CA 95030 United States", etc.
  const match = address.match(/\d{5}(-\d{4})?(?=\s|,|$)/)
  return match ? match[0] : null
}
